import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Gift, Check, MapPin, Phone, Mail, Globe, ArrowRight, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const PLAN_INFO = {
  decouverte: { label: "Découverte", price: 39, bottles: 2 },
  prestige: { label: "Prestige", price: 49, bottles: 3 },
};

function SubscriptionForm({ plan, onClose }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    deliveryMethod: "retrait", address: "", addressComplement: "",
    postalCode: "", city: "", firstDate: "", comment: "",
    isAdult: false, acceptTerms: false,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.isAdult || !form.acceptTerms) {
      toast.error("Merci de confirmer que vous êtes majeur et d'accepter les conditions.");
      return;
    }
    setLoading(true);
    try {
      const { data: order } = await api.post("/public/orders", { ...form, plan });
      const { data: pay } = await api.post(`/public/orders/${order.orderId}/payment`);
      if (pay.redirect_url) {
        window.location.assign(pay.redirect_url);
      } else {
        navigate(`/payment/return?ref=${order.merchantReference}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const info = PLAN_INFO[plan];

  return (
    <form onSubmit={submit} data-testid="subscription-form" className="space-y-4">
      <div className="rounded-xl bg-[#581825] text-[#FDFBF7] px-4 py-3 flex items-center justify-between">
        <span className="font-serif text-xl">Formule {info.label}</span>
        <span className="font-semibold">{info.price} € / mois · {info.bottles} bouteilles</span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Prénom</Label>
          <Input data-testid="input-client-firstname" required value={form.firstName}
            onChange={(e) => set("firstName", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Nom</Label>
          <Input data-testid="input-client-nom" required value={form.lastName}
            onChange={(e) => set("lastName", e.target.value)} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Email</Label>
        <Input data-testid="input-client-email" type="email" required value={form.email}
          onChange={(e) => set("email", e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label>Téléphone</Label>
        <Input data-testid="input-client-phone" required value={form.phone}
          onChange={(e) => set("phone", e.target.value)} />
      </div>

      <div className="space-y-2">
        <Label>Mode de récupération</Label>
        <RadioGroup data-testid="select-delivery-mode" value={form.deliveryMethod}
          onValueChange={(v) => set("deliveryMethod", v)} className="grid grid-cols-2 gap-3">
          {[
            { v: "retrait", t: "Retrait en magasin" },
            { v: "livraison", t: "Livraison à domicile" },
          ].map((o) => (
            <label key={o.v} htmlFor={`dm-${o.v}`}
              className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer transition-colors ${
                form.deliveryMethod === o.v ? "border-[#581825] bg-[#F5EFEB]" : "border-[#E5DEC9]"
              }`}>
              <RadioGroupItem value={o.v} id={`dm-${o.v}`} />
              <span className="text-sm font-medium">{o.t}</span>
            </label>
          ))}
        </RadioGroup>
      </div>

      {form.deliveryMethod === "livraison" && (
        <div className="space-y-3 rounded-xl bg-[#F5EFEB] p-3">
          <div className="space-y-1.5">
            <Label>Adresse</Label>
            <Input required value={form.address} onChange={(e) => set("address", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Complément d'adresse (facultatif)</Label>
            <Input value={form.addressComplement} onChange={(e) => set("addressComplement", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Code postal</Label>
              <Input required value={form.postalCode} onChange={(e) => set("postalCode", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Ville</Label>
              <Input required value={form.city} onChange={(e) => set("city", e.target.value)} />
            </div>
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <Label>Date souhaitée du premier {form.deliveryMethod === "livraison" ? "envoi" : "retrait"}</Label>
        <Input type="date" value={form.firstDate} onChange={(e) => set("firstDate", e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label>Commentaire (facultatif)</Label>
        <Textarea value={form.comment} onChange={(e) => set("comment", e.target.value)} rows={2} />
      </div>

      <label className="flex items-start gap-3 text-sm cursor-pointer">
        <Checkbox checked={form.isAdult} onCheckedChange={(v) => set("isAdult", !!v)} className="mt-0.5" />
        <span>Je certifie être majeur(e) (18 ans ou plus).</span>
      </label>
      <label className="flex items-start gap-3 text-sm cursor-pointer">
        <Checkbox checked={form.acceptTerms} onCheckedChange={(v) => set("acceptTerms", !!v)} className="mt-0.5" />
        <span>J'accepte l'engagement de 6 mois avec paiement mensuel.</span>
      </label>

      <div className="flex gap-3 pt-1">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
        <Button data-testid="btn-submit-subscription" type="submit" disabled={loading}
          className="flex-1 bg-[#581825] hover:bg-[#42101A] text-[#FDFBF7]">
          {loading ? "Redirection…" : `Payer en ligne · ${info.price} €`}
        </Button>
      </div>
    </form>
  );
}

function OfferCard({ id, testId, title, bottles, desc, price, onChoose, highlight }) {
  return (
    <motion.div
      data-testid={testId}
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={`relative rounded-2xl border-2 p-5 sm:p-7 flex flex-col ${
        highlight ? "border-[#581825] bg-white shadow-xl" : "border-[#581825]/40 bg-white/80"
      }`}
    >
      {highlight && (
        <span className="absolute -top-3 left-5 sm:left-7 rounded-full bg-[#581825] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#FDFBF7]">
          Le plus complet
        </span>
      )}
      <p className="text-xs uppercase tracking-[0.2em] text-[#6E675F]">Formule</p>
      <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-[#581825] mt-1">{title}</h3>
      <div className="flex items-baseline gap-2 mt-3 sm:mt-4">
        <span className="font-serif text-4xl sm:text-5xl font-bold text-[#110F0F]">{bottles}</span>
        <span className="text-base sm:text-lg text-[#6E675F]">bouteilles / mois</span>
      </div>
      <p className="text-sm sm:text-base text-[#6E675F] mt-3 sm:mt-4 leading-relaxed flex-1">{desc}</p>
      <div className="flex items-baseline gap-1 mt-4 sm:mt-6">
        <span className="font-serif text-4xl sm:text-5xl font-bold text-[#C5A059]">{price}</span>
        <span className="text-xl sm:text-2xl text-[#C5A059] font-serif">€</span>
        <span className="text-[#6E675F] ml-1 text-sm sm:text-base">/ mois</span>
      </div>
      <p className="text-xs sm:text-sm text-[#6E675F] mt-1">Engagement 6 mois · paiement mensuel</p>
      <Button data-testid={id} onClick={onChoose}
        className="mt-4 sm:mt-6 w-full bg-[#C5A059] hover:bg-[#B8934A] text-[#581825] font-semibold rounded-full h-11 sm:h-12 text-sm sm:text-base group">
        Je choisis la formule {title}
        <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
      </Button>
    </motion.div>
  );
}

export default function PublicLanding() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [offer, setOffer] = useState(null);

  useEffect(() => {
    api.get("/public/launch-offer").then((r) => setOffer(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7] bg-grain">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FDFBF7]/90 backdrop-blur border-b border-[#E5DEC9]">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="site-logo">
            <img
              src="/logo-caves-christophe.png"
              alt="Caves Christophe · Christophe Guédon"
              className="h-9 sm:h-10 w-auto"
            />
          </div>
          <button data-testid="btn-admin-login-trigger" onClick={() => navigate("/admin/login")}
            className="flex items-center gap-1.5 text-sm text-[#6E675F] hover:text-[#581825] transition-colors">
            <Lock className="w-4 h-4" /> Espace caviste
          </button>
        </div>
      </header>

      {/* Hero */}
      <section data-testid="public-hero-section" className="relative overflow-hidden">
        <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_cave-abonne/artifacts/jdio1sje_IMG_20260912_204411.webp"
          alt="Cave voûtée de la Cave Christophe" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 55%" }} />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-x-0 bottom-0 h-24 hero-fade" />
        <div className="relative max-w-4xl mx-auto px-5 py-16 sm:py-28 text-center text-[#FDFBF7]">
          {offer && offer.eligible && (
            <motion.div data-testid="launch-offer-badge"
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              className="inline-flex items-center gap-2 rounded-full bg-[#C5A059] text-[#110F0F] px-4 py-1.5 text-sm font-bold mb-6">
              <Gift className="w-4 h-4" />
              Offre de lancement · plus que {offer.spotsRemaining} places
            </motion.div>
          )}
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight animate-fade-up">
            La BOX VIN de la Cave Christophe
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-[#F5EFEB] animate-fade-up" style={{ animationDelay: "0.1s" }}>
            Votre caviste à Jonzac depuis 28 ans
          </p>
          <p className="mt-6 max-w-2xl mx-auto text-[#F5EFEB]/90 leading-relaxed animate-fade-up" style={{ animationDelay: "0.2s" }}>
            Recevez chaque mois chez vous ou retirez en magasin un coffret de découverte œnologique
            accompagné de ses fiches explicatives.
          </p>
        </div>
      </section>

      {/* Offers */}
      <section className="max-w-5xl mx-auto px-5 py-10 sm:py-20">
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-8">
          <OfferCard
            testId="offer-card-decouverte" id="btn-subscribe-decouverte"
            title="Découverte" bottles="2" price="39"
            desc="Une sélection mensuelle de 2 belles bouteilles pour explorer les vignobles."
            onChoose={() => setSelected("decouverte")}
          />
          <OfferCard
            testId="offer-card-prestige" id="btn-subscribe-prestige"
            title="Prestige" bottles="3" price="49" highlight
            desc="Une sélection mensuelle de 3 belles bouteilles pour découvrir différents domaines."
            onChoose={() => setSelected("prestige")}
          />
        </div>

        {/* Launch offer */}
        <div className="mt-6 sm:mt-10 rounded-2xl border border-[#C5A059]/50 bg-[#F5EFEB] p-4 sm:p-6 flex items-start gap-3 sm:gap-4">
          <div className="rounded-full bg-[#C5A059]/20 p-2.5 sm:p-3 shrink-0">
            <Gift className="w-5 h-5 sm:w-6 sm:h-6 text-[#581825]" />
          </div>
          <div>
            <h4 className="font-serif text-lg sm:text-xl font-semibold text-[#581825]">Offre de lancement spéciale</h4>
            <p className="text-sm sm:text-base text-[#6E675F] mt-1">
              Pour les 40 premiers abonnés : une bouteille surprise supplémentaire !
              {offer && (offer.eligible
                ? ` Il reste ${offer.spotsRemaining} place${offer.spotsRemaining > 1 ? "s" : ""}.`
                : " Offre désormais complète.")}
            </p>
          </div>
        </div>

        <ul className="mt-6 sm:mt-10 grid sm:grid-cols-3 gap-3 sm:gap-4">
          {["Sélection par un caviste passionné", "Fiches œnologiques incluses", "Retrait à Jonzac ou livraison"].map((t) => (
            <li key={t} className="flex items-center gap-2 text-[#1A1818]">
              <Check className="w-5 h-5 text-[#581825]" /> <span className="text-sm">{t}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Footer */}
      <footer className="bg-[#110F0F] text-[#F5EFEB]">
        <div className="max-w-4xl mx-auto px-5 py-14 text-center">
          <h4 className="font-serif text-2xl text-[#C5A059] font-semibold">Caves Christophe</h4>
          <p className="mt-2 text-[#F5EFEB]/80">Christophe Guédon — Caviste</p>
          <div className="mt-6 space-y-2 text-sm text-[#F5EFEB]/80">
            <p className="flex items-center justify-center gap-2"><MapPin className="w-4 h-4 text-[#C5A059]" /> 14 rue de Verdun, 17500 Jonzac</p>
            <p className="flex items-center justify-center gap-2"><Phone className="w-4 h-4 text-[#C5A059]" /> 06 07 15 59 70</p>
            <p className="flex items-center justify-center gap-2"><Mail className="w-4 h-4 text-[#C5A059]" /> caves.christophe@wanadoo.fr</p>
            <p className="flex items-center justify-center gap-2"><Globe className="w-4 h-4 text-[#C5A059]" /> caves-christophe.fr</p>
          </div>
        </div>
      </footer>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="w-full max-w-full h-[100dvh] max-h-[100dvh] top-0 left-0 translate-x-0 translate-y-0 rounded-none overflow-y-auto no-scrollbar bg-[#FDFBF7] p-5 pb-56 sm:p-6 sm:pb-6 sm:max-w-lg sm:h-auto sm:max-h-[90vh] sm:top-[50%] sm:left-[50%] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#581825]">Formulaire d'abonnement</DialogTitle>
          </DialogHeader>
          {selected && <SubscriptionForm plan={selected} onClose={() => setSelected(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
